import { Maybe, PinwheelExperienceInput } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { useSavingsTracker } from 'features/accounts/hooks/useSavingsTracker';
import DirectDepositLoader from 'features/directDeposit/screens/DirectDepositLoader';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Tracker from 'shared/components/vxl/Tracker/Tracker';
import ExpressiveXSText from 'shared/components/vxl/typography/expressive/ExpressiveXSText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkSmText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkSmText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import useDirectDepositLoadingComponent from 'shared/hooks/pinwheel/useDirectDepositLoadingComponent/useDirectDepositLoadingComponent';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { showZendeskArticle } from 'shared/utils/zendesk/zendeskUtils';

const SavingTrackerScreen = () => {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>() ?? {};
  const { t: translatedText } = useTranslation('accounts', {
    keyPrefix: 'automaticSavingsTracker',
  });
  const { initiatePinwheel, isPinwheelLoading } = usePinwheel();

  const {
    shouldRedirectToDDManagementPage,
    shouldLaunchPinwheelSDK,
    isLoading: pinwheelFlowLoading,
    refetchDdManagementFlow,
    handleDdManagementLinkTokenFailure,
  } = useDirectDepositManagementFlow();

  const { defaultTitle } = useDefaultErrorStrings();

  const { savingTrackerData, directDepositData, fetching, error, refetch } =
    useSavingsTracker(id ?? '');

  const handleHyperLinkClickAnalytics = usePressableAnalytics({
    analyticsId: 'savingFaqClicked',
    customAttributes: {
      faq_name: 'qualifying_direct_deposits',
    },
  });

  const { skipIntroScreen } = useDirectDepositLoadingComponent();

  const progressValue = useMemo(() => {
    const customerValue = Number(
      directDepositData?.requirementValue?.customerValue?.amount ?? 0,
    );
    const thresholdValue = Number(
      directDepositData?.requirementValue?.thresholdValue?.amount ?? 0,
    );
    return thresholdValue !== 0 ? customerValue / thresholdValue : 0;
  }, [directDepositData]);

  const onPressHyperLink = (zendeskId: Maybe<string> | undefined) => {
    handleHyperLinkClickAnalytics();
    showZendeskArticle(Number(zendeskId));
  };

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />

      <ScrollView contentContainerStyle={styles.scrollContainer}>
        <View style={styles.screenHeader}>
          <View style={styles.mediumPaddingBottom}>
            <TitleLgText numberOfLines={2}>
              {savingTrackerData?.title ?? ''}
            </TitleLgText>
          </View>
          <View style={styles.row}>
            <View style={styles.trackLabelOne}>
              <BodySmText numberOfLines={1} style={styles.titleOneWidth}>
                {translatedText('trackLabelOneTitle')}
              </BodySmText>
              <ExpressiveXSText numberOfLines={1}>
                {directDepositData?.requirementValue?.customerValue
                  ?.formatted ?? ''}
              </ExpressiveXSText>
            </View>
            <View style={styles.trackLabelTwo}>
              <BodySmText numberOfLines={1} style={styles.textColorSecondary}>
                {translatedText('trackLabelTwoTitle')}
              </BodySmText>
              <ExpressiveXSText
                numberOfLines={1}
                style={styles.textColorSecondary}
              >
                {directDepositData?.requirementValue?.thresholdValue
                  ?.formatted ?? ''}
              </ExpressiveXSText>
            </View>
          </View>
          <View style={styles.trackerWrapper}>
            <Tracker
              progressValue={progressValue}
              backgroundTexture="PINK"
              hasAnimation
              backgroundColor={theme.color.background.tertiary}
              trackerType="SIMPLE"
            />
          </View>
          {directDepositData?.cta ? (
            <Button
              label={directDepositData.cta}
              onPress={() => {
                if (
                  shouldLaunchPinwheelSDK ||
                  !shouldRedirectToDDManagementPage
                ) {
                  initiatePinwheel(
                    shouldLaunchPinwheelSDK
                      ? {
                          input: {
                            experience:
                              PinwheelExperienceInput.DirectDepositSwitch,
                            skipIntroScreen,
                          },
                          launchBehavior: skipIntroScreen
                            ? { delayAfterTokenMs: 3000 }
                            : undefined,
                          onOutcome: refetchDdManagementFlow,
                          onLinkTokenError: handleDdManagementLinkTokenFailure,
                        }
                      : undefined,
                  );
                } else {
                  router.push(
                    '/move-money/direct-deposit/direct-deposit-management',
                  );
                }
              }}
              analyticsId="ctaClicked"
            />
          ) : null}
        </View>
        <View style={styles.body}>
          <TitleMdText numberOfLines={2} style={styles.mediumMarginBottom}>
            {savingTrackerData?.description ?? ''}
          </TitleMdText>
          {savingTrackerData?.requirements?.map((requirement) => (
            <View
              key={requirement.id}
              style={[styles.row, styles.mediumMarginBottom]}
            >
              <View style={styles.successIconWrapper}>
                <Icon
                  iconKey="SUCCESS"
                  iconColor={
                    requirement.isEligible
                      ? theme.color.status.positive
                      : theme.color.text.tertiary
                  }
                />
              </View>
              <View style={styles.textWrapper}>
                <BodySmText numberOfLines={3}>
                  {requirement.description + '\n'}
                  {requirement.qualificationContentText ? (
                    <HyperlinkSmText
                      numberOfLines={1}
                      onPress={() => onPressHyperLink(requirement.zendeskId)}
                    >
                      {requirement.qualificationContentText}
                    </HyperlinkSmText>
                  ) : null}
                </BodySmText>
              </View>
            </View>
          ))}

          <View style={styles.dividerWrapper}>
            <HairLineDivider />
          </View>
          <BodyXSText numberOfLines={4} style={styles.mediumMarginBottom}>
            {savingTrackerData?.banners?.[0]?.message ?? ''}
          </BodyXSText>
          <BodyXSText
            numberOfLines={3}
            style={[styles.textColorSecondary, styles.mediumMarginBottom]}
          >
            {`${savingTrackerData?.metricUpdatedDescription ?? ''}. ${savingTrackerData?.lastUpdateTimeDescription ?? ''}`}
          </BodyXSText>
        </View>
        <ScreenTransitionOverlay
          isLoading={fetching || isPinwheelLoading || pinwheelFlowLoading}
          LoadingComponent={
            <DirectDepositLoader
              isLoadingConditions={fetching}
              isPinwheelLoading={isPinwheelLoading || pinwheelFlowLoading}
            />
          }
          ErrorComponent={
            error ? (
              <DefaultPageDownErrorMessage
                error={error}
                title={defaultTitle}
                message={error.message ?? ''}
                tryAgainAction={refetch}
              />
            ) : null
          }
        />
      </ScrollView>
    </View>
  );
};

export default SavingTrackerScreen;

const styles = StyleSheet.create({
  body: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    padding: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.surface,
    flex: 1,
  },
  dividerWrapper: {
    paddingVertical: theme.spacing.m,
  },
  mediumMarginBottom: {
    marginBottom: theme.spacing.m,
  },
  mediumPaddingBottom: {
    paddingBottom: theme.spacing.m,
  },
  row: {
    flexDirection: 'row',
  },
  screenHeader: {
    backgroundColor: theme.color.background.primary,
    padding: theme.spacing.m,
  },
  scrollContainer: {
    flexGrow: 1,
  },
  successIconWrapper: {
    marginRight: theme.spacing.xs,
  },
  textColorSecondary: {
    color: theme.color.text.secondary,
  },
  textWrapper: {
    flexShrink: 1,
  },
  titleOneWidth: {
    width: 200,
  },
  trackLabelOne: {
    flex: 1,
  },
  trackLabelTwo: {
    alignItems: 'flex-end',
    flex: 1,
  },
  trackerWrapper: {
    paddingBottom: theme.spacing.xl,
    paddingTop: theme.spacing.m,
  },
});
